import { Icon } from "@constellation-academy/academies-lib-webapp";
import { stripHtml } from "@constellation-academy/academies-lib-webapp/utils";
import { IconMap } from "@constellation-academy/epic-ui";
import { useState } from "react";
import { useFragment } from "react-relay";
import { MultipleChoiceGapTextStatus } from "@components/multiple-choice-gap-text/multiple-choice-gap-text.interface";
import {
	answerText,
	answerTextCorrect,
} from "@components/multiple-choice-gap-text/multiple-choice-gap-text.styles";

import {
	multipleChoiceContainer,
	multipleChoiceContentWrapper,
	multipleChoiceCorrectAnswer,
	multipleChoiceIconWrapper,
	multipleChoiceList,
	multipleChoiceListItem,
	multipleChoiceWrongAnswer,
} from "@components/multiple-choice-learn-element/multiple-choice-learn-element.styles";
import { multipleChoiceLearnElement_MultipleChoiceLearnElementFragment$data } from "@relay/multipleChoiceLearnElement_MultipleChoiceLearnElementFragment.graphql";
import {
	answerWrapperClass,
	correctAnswerTitle,
	correctAnswerWrapper,
	wrongAnswerTitleClass,
} from "@screens/elearning-content-submission/parts/lesson-insights/lesson-insights.styles";
import { error100, success100 } from "@themes/colors";
import { MULTIPLE_CHOICE_LEARN_ELEMENT_FRAGMENT } from "./multiple-choice-learn-element.graphql";
import {
	AnswerTypeInterface,
	MultipleChoiceLearnElementProps,
} from "./multiple-choice-learn-element.types";

export const MultipleChoiceLearnElement = ({
	isResultWrong,
	getStatusForIndex,
	learnElementFragmentRef,
	checkedIndices,
}: MultipleChoiceLearnElementProps) => {
	const element = useFragment(
		MULTIPLE_CHOICE_LEARN_ELEMENT_FRAGMENT,
		learnElementFragmentRef ?? null,
	);
	const [isCorrectShown, setIsCorrectShown] = useState(false);
	const answers = element?.answerOptions ?? [];
	const getAnswerByStatus = (
		statuses: MultipleChoiceGapTextStatus[],
	): (AnswerTypeInterface | undefined)[] => {
		return (
			answers?.map((answer, index) => {
				const status = getStatusForIndex?.(index);
				return status && statuses.includes(status) ? answer : undefined;
			}) ?? []
		);
	};

	const userAnswer = answers?.filter((_, index) => checkedIndices?.includes(index));
	const handleAnswerClick = () => {
		if (isResultWrong) {
			setIsCorrectShown((prev) => !prev);
		}
	};

	const handleHideCorrectAnswers = () => setIsCorrectShown(false);

	const getCorrectTextColor = (status: number) => {
		return status === 1 ? multipleChoiceCorrectAnswer : multipleChoiceWrongAnswer;
	};

	const renderAnswerItem = (
		answer: multipleChoiceLearnElement_MultipleChoiceLearnElementFragment$data["answerOptions"][number],
		index: number,
		getStatusForIndex?: (index: number) => MultipleChoiceGapTextStatus,
		isSingleQuestion = false,
	) => {
		if (!answer) return null;

		const status = getStatusForIndex
			? getStatusForIndex(index)
			: MultipleChoiceGapTextStatus.Default;
		return (
			<li key={answer.answer}>
				<span
					// if result is correct and it's just one question, return true, else, check for status
					className={`${answerText} ${getCorrectTextColor(
						isSingleQuestion && !isResultWrong ? 1 : status,
					)}`}
				>
					{stripHtml(answer.answer)}
				</span>
			</li>
		);
	};

	function renderMultipleChoiceAnswers(answers?: AnswerTypeInterface[] | undefined) {
		if (!answers || answers.length === 0) return null;

		return answers.map((answer) => {
			if (!answer) return null;
			return (
				<div className={multipleChoiceListItem} key={answer.answer}>
					<span className={`${answerText} ${answerTextCorrect}`}>
						• {stripHtml(answer.answer)}
					</span>
				</div>
			);
		});
	}

	return (
		<>
			<h6 className={wrongAnswerTitleClass}>Deine Antwort</h6>
			<div className={answerWrapperClass} onClick={handleAnswerClick}>
				<ul>
					{userAnswer?.map((answer, index, currentArray) =>
						renderAnswerItem(
							answer,
							index,
							getStatusForIndex,
							currentArray?.length === 1,
						),
					)}
				</ul>

				{isResultWrong && (
					<Icon
						icon={(isCorrectShown ? "arrowUp" : "arrowDown") as keyof IconMap}
						color={error100}
						sizeRem={0.875}
					/>
				)}
			</div>

			{isCorrectShown && (
				<div className={correctAnswerWrapper} onClick={handleHideCorrectAnswers}>
					<div className={multipleChoiceContainer}>
						<div className={multipleChoiceIconWrapper}>
							<Icon
								icon={"arrowUp" as keyof IconMap}
								color={success100}
								sizeRem={0.875}
							/>
						</div>

						<div className={multipleChoiceContentWrapper}>
							<h6 className={correctAnswerTitle}>Lösung</h6>
							<ul className={multipleChoiceList}>
								{renderMultipleChoiceAnswers(
									getAnswerByStatus([
										MultipleChoiceGapTextStatus.Correct,
										MultipleChoiceGapTextStatus.ActuallyCorrect,
									]) as AnswerTypeInterface[],
								)}
							</ul>
						</div>
					</div>
				</div>
			)}
		</>
	);
};
